import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CommonService } from '@fuse/services/common.service';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { environment } from 'environments/environment';
import { ApiEnvelope, EmailTemplateApiError, SaveRequest, TemplateDetail, TemplateListItem } from './models';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

export interface ApiResult<T> {
    data: T;
    message: string;
}

/**
 * Unwraps the `{status, message, data}` envelope. The app's HTTP interceptor turns
 * HTTP errors into normal values, so an HttpErrorResponse can arrive here as well.
 */
export function unwrapEnvelope<T>(response: ApiEnvelope<T> | HttpErrorResponse | null): T {
    if (response instanceof HttpErrorResponse) {
        throw new EmailTemplateApiError(response.status || 0, response.error?.message || GENERIC_ERROR);
    }
    if (!response || typeof response.status !== 'number') {
        throw new EmailTemplateApiError(0, GENERIC_ERROR);
    }
    if (response.status !== 200) {
        throw new EmailTemplateApiError(response.status, response.message || GENERIC_ERROR);
    }
    return response.data;
}

@Injectable({
    providedIn: 'root'
})
export class EmailTemplateApiService {

    private readonly baseUrl = environment.apiURL + 'admin/customEmailTemplate/';

    constructor(
        private _httpClient: HttpClient,
        private _commonService: CommonService,
        private _fuseProgressBarService: FuseProgressBarService,
    ) { }

    getAll(): Promise<TemplateListItem[]> {
        return this.request<TemplateListItem[]>(
            this._httpClient.get<ApiEnvelope<TemplateListItem[]>>(this.baseUrl + 'getAll', this._commonService.getHeader())
        ).then(result => result.data || []);
    }

    get(templateKey: string): Promise<TemplateDetail> {
        const params = new HttpParams().set('templateKey', templateKey);
        return this.request<TemplateDetail>(
            this._httpClient.get<ApiEnvelope<TemplateDetail>>(this.baseUrl + 'get', { ...this._commonService.getHeader(), params })
        ).then(result => result.data);
    }

    /** Upsert of my version. Send only the fields that changed. */
    save(body: SaveRequest): Promise<ApiResult<TemplateDetail>> {
        return this.request<TemplateDetail>(
            this._httpClient.post<ApiEnvelope<TemplateDetail>>(this.baseUrl + 'save', body, this._commonService.getHeader())
        );
    }

    /** Deletes my version. `data` is the detail as it now stands, or null if the platform template is gone too. */
    delete(templateKey: string): Promise<ApiResult<TemplateDetail | null>> {
        return this.request<TemplateDetail | []>(
            this._httpClient.post<ApiEnvelope<TemplateDetail | []>>(this.baseUrl + 'delete', { templateKey }, this._commonService.getHeader())
        ).then(result => ({
            message: result.message,
            data: Array.isArray(result.data) || !result.data ? null : result.data,
        }));
    }

    /** Uploads an image for the email body and resolves to its public (unsigned) URL. */
    async uploadImage(file: File): Promise<string> {
        const dotIndex = file.name.lastIndexOf('.');
        const fileExt = dotIndex >= 0
            ? file.name.slice(dotIndex).toLowerCase()
            : '.' + String(file.type).slice(String(file.type).indexOf('/') + 1);

        const presigned: any = await this._commonService.getPresignedUrlAPI({
            fileExt,
            fileName: 'template_',
            folderName: 'templateImage',
            mimeType: file.type,
        });
        const data = unwrapEnvelope<any>(presigned);

        const uploaded = await this._httpClient.put(data.presignedUrl, file, {
            headers: new HttpHeaders({ 'Content-Type': file.type }),
            responseType: 'text',
        }).toPromise();
        if ((uploaded as any) instanceof HttpErrorResponse) {
            throw new EmailTemplateApiError((uploaded as any).status || 0, 'The image could not be uploaded.');
        }

        await this._commonService.getFileUploadStatus({ id: data.id, status: 1 });
        return data.imageUploadUrl;
    }

    private request<T>(call: Observable<ApiEnvelope<T>>): Promise<ApiResult<T>> {
        this._fuseProgressBarService.show();
        return call.toPromise()
            .then(response => ({ data: unwrapEnvelope<T>(response), message: response.message }), error => ({ data: unwrapEnvelope<T>(error), message: '' }))
            .finally(() => this._fuseProgressBarService.hide());
    }
}
